<script lang="ts">
	/**
	 * Where visitors are, on a world map (§12.10) — the country breakdown drawn as a choropleth.
	 *
	 * **One hue, light to dark**, from the chart theme's single-series colour: a count is a quantity,
	 * and a quantity reads as a ramp, never as categorical hues (which would invite comparing two
	 * countries' colours as if they meant something different). Countries with nothing recorded keep
	 * the neutral axis grey, so "none" is distinct from "few".
	 *
	 * It is decoration over the table beside it, never instead of it: the map is imported
	 * **dynamically** and a failure — jsdom has no SVG layout, a blocked chunk — leaves nothing
	 * drawn rather than an error, because the same numbers are already on screen as rows. The same
	 * reasoning as `ApexChart`: a chart that can fail must fail into the table, not over it.
	 */

	import { onDestroy } from 'svelte';
	import { colorTheme } from '../../AbstractID';
	import { chartTheme } from './chartTheme';
	import { countryName } from '../../Countries';

	/** ISO-3166 alpha-2 code → the count to shade by. Unknown or blank codes are ignored. */
	export let values: Record<string, number> = {};
	/** What the count is, for the tooltip — "visits", "events". */
	export let measureLabel: string = 'visits';

	let container: HTMLDivElement | null = null;
	let map: { destroy?: () => void } | null = null;
	let failed = false;

	/** A tint of `color` toward `ground`, for the low end of the ramp. */
	function mix(color: string, ground: string, weight: number): string {
		const parse = (hex: string): number[] => [1, 3, 5].map(index => parseInt(hex.slice(index, index + 2), 16));
		const [a, b] = [parse(color), parse(ground)];
		return `#${a
			.map((channel, index) => Math.round(channel * weight + b[index] * (1 - weight)).toString(16).padStart(2, '0'))
			.join('')}`;
	}

	async function draw(into: HTMLDivElement, shading: Record<string, number>): Promise<void> {
		try {
			map?.destroy?.();
			map = null;
			into.innerHTML = '';
			const theme = chartTheme();
			const module = await import('jsvectormap');
			const JsVectorMap = (module as unknown as { default: new (options: Record<string, unknown>) => { destroy?: () => void } }).default;
			// The map file registers itself on a global, which is how the library ships it.
			(window as unknown as { jsVectorMap: unknown }).jsVectorMap = JsVectorMap;
			await import('jsvectormap/dist/maps/world.js');
			await import('jsvectormap/dist/jsvectormap.css');

			const known = Object.fromEntries(
				Object.entries(shading).filter(([code, count]) => /^[A-Z]{2}$/.test(code) && count > 0)
			);
			map = new JsVectorMap({
				selector: into,
				map: 'world',
				zoomButtons: false,
				zoomOnScroll: false,
				backgroundColor: 'transparent',
				regionStyle: {
					initial: { fill: theme.axis, stroke: theme.surface, strokeWidth: 0.5 },
					hover: { fillOpacity: 0.8, cursor: 'default' }
				},
				series: {
					regions: [
						{
							attribute: 'fill',
							values: known,
							scale: [mix(theme.single, theme.surface, 0.25), theme.single],
							normalizeFunction: 'polynomial'
						}
					]
				},
				onRegionTooltipShow(_event: Event, tooltip: { text: (text: string) => void }, code: string) {
					const count = known[code] ?? 0;
					tooltip.text(`${countryName(code)}: ${count.toLocaleString()} ${measureLabel}`);
				}
			});
			failed = false;
		} catch {
			failed = true;
		}
	}

	// Redrawn when the numbers change and when the theme does — the fills are computed, not CSS.
	$: if (container !== null) void draw(container, values), $colorTheme;

	onDestroy(() => {
		map?.destroy?.();
	});
</script>

{#if !failed}
	<div bind:this={container} class="w-full h-72" role="img" aria-label="Map of {measureLabel} by country" />
{/if}
